<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { toIsoDate } from '../utils/format'
import type { CurrencyOption, FinancialAccountType, Option } from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const {
  getFinancialAccount,
  createFinancialAccount,
  updateFinancialAccount,
  listCurrencies,
  listWorkplaceOptions,
} = useFinanceApi()

const accountId = computed(() => (route.params.id ? String(route.params.id) : ''))
const isEdit = computed(() => accountId.value !== '')

const currencies = ref<CurrencyOption[]>([])
const workplaces = ref<Option[]>([])
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const formError = ref('')
const savedMessage = ref('')

const accountTypes: FinancialAccountType[] = ['cashbox', 'bank', 'card', 'clearing']

const form = ref({
  account_code: '',
  name: '',
  account_type: 'cashbox' as FinancialAccountType,
  currency_code: '',
  opening_balance: 0,
  opening_balance_date: toIsoDate(),
  branch_agent_id: '' as string | number,
  department_agent_id: '' as string | number,
  is_active: 1 as 0 | 1,
})

const validationError = computed(() => {
  if (!form.value.account_code.trim()) {
    return t('finance.common.requiredField', { field: t('finance.accounts.accountCode') })
  }
  if (!form.value.name.trim()) {
    return t('finance.common.requiredField', { field: t('finance.accounts.accountName') })
  }
  if (!form.value.currency_code) {
    return t('finance.common.requiredField', { field: t('finance.common.currency') })
  }
  if (!form.value.opening_balance_date) {
    return t('finance.common.requiredField', { field: t('finance.common.date') })
  }
  return ''
})

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    const [currencyList, workplaceList] = await Promise.all([
      listCurrencies(),
      listWorkplaceOptions(),
    ])
    currencies.value = currencyList
    workplaces.value = workplaceList
    if (!form.value.currency_code) {
      form.value.currency_code =
        currencyList.find((currency) => currency.is_base === 1)?.code || currencyList[0]?.code || ''
    }

    if (isEdit.value) {
      const account = await getFinancialAccount(accountId.value)
      form.value = {
        account_code: account.account_code,
        name: account.name,
        account_type: account.account_type,
        currency_code: account.currency_code,
        opening_balance: Number(account.opening_balance ?? 0),
        opening_balance_date: account.opening_balance_date || toIsoDate(),
        branch_agent_id: account.branch_agent_id ?? '',
        department_agent_id: account.department_agent_id ?? '',
        is_active: account.is_active,
      }
    }
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function submit() {
  if (validationError.value) {
    formError.value = validationError.value
    return
  }
  saving.value = true
  formError.value = ''
  savedMessage.value = ''
  try {
    const payload = {
      account_code: form.value.account_code.trim(),
      name: form.value.name.trim(),
      account_type: form.value.account_type,
      currency_code: form.value.currency_code,
      opening_balance: Number(form.value.opening_balance) || 0,
      opening_balance_date: form.value.opening_balance_date,
      branch_agent_id: form.value.branch_agent_id || undefined,
      department_agent_id: form.value.department_agent_id || undefined,
      is_active: form.value.is_active,
    }
    if (isEdit.value) {
      const account = await updateFinancialAccount(accountId.value, payload)
      savedMessage.value = t('finance.accounts.accountSaved')
      await router.push(`/treasury/accounts/${account.id}`)
    } else {
      const account = await createFinancialAccount(payload)
      savedMessage.value = t('finance.accounts.accountSaved')
      await router.push(`/treasury/accounts/${account.id}`)
    }
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/treasury/accounts">← {{ t('finance.accounts.title') }}</RouterLink>
    <h2>{{ isEdit ? t('finance.accounts.editAccount') : t('finance.accounts.newAccount') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="loadError" class="state-message error">
    <p>{{ loadError }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <form v-else class="form-card" @submit.prevent="submit">
    <div class="form-grid">
      <div class="field">
        <label for="account-code">{{ t('finance.accounts.accountCode') }}</label>
        <input
          id="account-code"
          v-model="form.account_code"
          type="text"
          autocomplete="off"
          placeholder="CASH-01"
        >
      </div>
      <div class="field">
        <label for="account-name">{{ t('finance.accounts.accountName') }}</label>
        <input id="account-name" v-model="form.name" type="text" autocomplete="off">
      </div>
      <div class="field">
        <label for="account-type">{{ t('finance.accounts.accountType') }}</label>
        <select id="account-type" v-model="form.account_type">
          <option v-for="type in accountTypes" :key="type" :value="type">
            {{ t(`finance.accountType.${type}`) }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="account-currency">{{ t('finance.common.currency') }}</label>
        <select id="account-currency" v-model="form.currency_code">
          <option value="">{{ t('finance.common.allCurrencies') }}</option>
          <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
            {{ currency.code }} — {{ currency.name }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="account-opening">{{ t('finance.common.openingBalance') }}</label>
        <input
          id="account-opening"
          v-model.number="form.opening_balance"
          type="number"
          step="0.01"
        >
      </div>
      <div class="field">
        <label for="account-opening-date">{{ t('finance.common.date') }}</label>
        <input id="account-opening-date" v-model="form.opening_balance_date" type="date">
      </div>
      <div class="field">
        <label for="account-department">{{ t('finance.accounts.department') }}</label>
        <select id="account-department" v-model="form.department_agent_id">
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="workplace in workplaces" :key="workplace.id" :value="workplace.id">
            {{ workplace.name }}
          </option>
        </select>
      </div>
      <div class="field">
        <label for="account-branch">{{ t('finance.accounts.branch') }}</label>
        <select id="account-branch" v-model="form.branch_agent_id">
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="workplace in workplaces" :key="workplace.id" :value="workplace.id">
            {{ workplace.name }}
          </option>
        </select>
      </div>
      <label class="check-field">
        <input v-model="form.is_active" type="checkbox" :true-value="1" :false-value="0">
        {{ t('finance.common.active') }}
      </label>
    </div>

    <p v-if="formError" class="error-inline">{{ formError }}</p>
    <p v-if="savedMessage" class="action-message">{{ savedMessage }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        {{ saving ? t('finance.common.saving') : t('finance.common.save') }}
      </button>
      <RouterLink class="btn-secondary" to="/treasury/accounts">
        {{ t('finance.common.cancel') }}
      </RouterLink>
    </div>
  </form>
</template>
